import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
  RefreshCw,
  Search,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Filter,
  Smartphone,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Activity,
} from 'lucide-react';
import DateRangeFilter from '../components/filters/DateRangeFilter';
import {
  appendDateParams,
} from '../utils/dateRangeHelpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------
const fmtCurrency = (v) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n));
};

const fmtNumber = (v, decimals = 1) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(decimals) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(decimals) + 'K';
  return n.toFixed(decimals);
};

const fmtPct = (v) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return n.toFixed(2) + '%';
};

const fmtRoas = (v) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return n.toFixed(2) + 'x';
};

// ---------------------------------------------------------------------------
// Channel config
// ---------------------------------------------------------------------------
const CHANNEL_CONFIG = {
  google: {
    label: 'Google Ads',
    icon: '/google_ads-512.webp',
    color: '#4285F4',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
  },
  meta: {
    label: 'Meta',
    icon: '/meta-logo.svg',
    color: '#1877F2',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-700',
  },
  tiktok: {
    label: 'TikTok',
    icon: '/tiktokv4.svg',
    color: '#010101',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    textColor: 'text-gray-700',
  },
};

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------
const KpiCard = ({ icon: Icon, label, value, subtitle, subtitleValue }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-2">
    <div className="flex items-center gap-2 text-gray-500">
      <Icon className="w-4 h-4" />
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
    {subtitle && (
      <div className="text-xs text-gray-500">
        {subtitle}: <span className="font-semibold text-gray-700">{subtitleValue}</span>
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-100 rounded-lg ${className}`} />
);

// ---------------------------------------------------------------------------
// Channel Card
// ---------------------------------------------------------------------------
const ChannelCard = ({ channel }) => {
  const cfg = CHANNEL_CONFIG[channel.key] || {};

  const metricRows = [
    [
      { label: 'Spend', value: `₺${fmtCurrency(channel.spend)}`, note: true },
      { label: 'Installs', value: fmtNumber(channel.adjustInstalls, 0) },
    ],
    [
      { label: 'Impr', value: fmtNumber(channel.impressions) },
      { label: 'Sessions', value: fmtNumber(channel.adjustSessions) },
    ],
    [
      { label: 'Clicks', value: fmtNumber(channel.clicks) },
      { label: 'Purchases', value: fmtNumber(channel.adjustPurchases) },
    ],
    [
      { label: 'CTR / CPC', value: `${fmtPct(channel.ctr)} / ₺${fmtCurrency(channel.cpc)}` },
      { label: 'Revenue / ROAS', value: `₺${fmtCurrency(channel.adjustRevenue)} / ${fmtRoas(channel.roas)}` },
    ],
  ];

  return (
    <div className={`bg-white rounded-xl shadow-sm border ${cfg.borderColor || 'border-gray-100'} overflow-hidden`}>
      {/* Card header */}
      <div className={`${cfg.bgColor || 'bg-gray-50'} px-4 py-3 flex items-center gap-3 border-b ${cfg.borderColor || 'border-gray-100'}`}>
        <img
          src={cfg.icon}
          alt={channel.label}
          className="w-6 h-6 object-contain"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <span className={`text-sm font-semibold ${cfg.textColor || 'text-gray-700'}`}>{channel.label}</span>
      </div>

      {/* Metric grid */}
      <div className="p-4">
        {metricRows.map((row, ri) => (
          <div key={ri} className={`grid grid-cols-2 gap-2 ${ri < metricRows.length - 1 ? 'mb-3 pb-3 border-b border-gray-50' : ''}`}>
            {row.map((cell, ci) => (
              <div key={ci}>
                <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">
                  {cell.label}
                  {cell.note && (
                    <span
                      className="ml-1 text-amber-500 cursor-help"
                      title="spend formula update pending"
                    >
                      *
                    </span>
                  )}
                </div>
                <div className="text-sm font-semibold text-gray-800 tabular-nums">{cell.value}</div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* CPI / CPP footer */}
      <div className={`${cfg.bgColor || 'bg-gray-50'} px-4 py-2 flex gap-4 text-xs text-gray-500 border-t ${cfg.borderColor || 'border-gray-100'}`}>
        <span>CPI: <strong className="text-gray-700">₺{fmtCurrency(channel.cpi)}</strong></span>
        <span>CPP: <strong className="text-gray-700">₺{fmtCurrency(channel.cpp)}</strong></span>
        <span>CR: <strong className="text-gray-700">{fmtPct(channel.cr)}</strong></span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Trend Chart
// ---------------------------------------------------------------------------
const TrendChart = ({ trend }) => {
  if (!trend || trend.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        No trend data available
      </div>
    );
  }

  const labels = trend.map((t) => {
    const d = new Date(t.date);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  });

  const data = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Google Spend',
        data: trend.map((t) => t.googleSpend || 0),
        backgroundColor: 'rgba(66,133,244,0.7)',
        stack: 'spend',
        yAxisID: 'ySpend',
        order: 2,
      },
      {
        type: 'bar',
        label: 'Meta Spend',
        data: trend.map((t) => t.metaSpend || 0),
        backgroundColor: 'rgba(24,119,242,0.7)',
        stack: 'spend',
        yAxisID: 'ySpend',
        order: 2,
      },
      {
        type: 'bar',
        label: 'TikTok Spend',
        data: trend.map((t) => t.tiktokSpend || 0),
        backgroundColor: 'rgba(1,1,1,0.65)',
        stack: 'spend',
        yAxisID: 'ySpend',
        order: 2,
      },
      {
        type: 'line',
        label: 'Installs',
        data: trend.map((t) => t.adjustInstalls || 0),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.1)',
        borderWidth: 2,
        pointRadius: 2,
        fill: false,
        yAxisID: 'yInstalls',
        tension: 0.3,
        order: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: { boxWidth: 12, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset.label || '';
            const v = ctx.parsed.y;
            if (label === 'Installs') return `${label}: ${fmtNumber(v, 0)}`;
            return `${label}: ₺${fmtCurrency(v)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, maxTicksLimit: 20 },
      },
      ySpend: {
        type: 'linear',
        position: 'left',
        stacked: true,
        ticks: {
          font: { size: 10 },
          callback: (v) => `₺${fmtNumber(v)}`,
        },
        grid: { color: 'rgba(0,0,0,0.04)' },
      },
      yInstalls: {
        type: 'linear',
        position: 'right',
        grid: { drawOnChartArea: false },
        ticks: {
          font: { size: 10 },
          callback: (v) => fmtNumber(v, 0),
          color: '#10b981',
        },
      },
    },
  };

  return <Bar data={data} options={options} />;
};

// ---------------------------------------------------------------------------
// Campaign Table
// ---------------------------------------------------------------------------
const SORT_COLS = ['spend', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions'];

const CampaignTable = ({ campaigns }) => {
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [sortCol, setSortCol] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const handleSort = (col) => {
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
    setPage(0);
  };

  const filtered = (campaigns || [])
    .filter((c) => {
      if (channelFilter !== 'all' && c.channel !== channelFilter) return false;
      if (search && !c.campaignName?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const av = a[sortCol] ?? 0;
      const bv = b[sortCol] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronDown className="w-3 h-3 text-gray-300 inline ml-0.5" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-blue-500 inline ml-0.5" />
      : <ChevronDown className="w-3 h-3 text-blue-500 inline ml-0.5" />;
  };

  const colHeader = (label, col) => (
    <th
      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 whitespace-nowrap"
      onClick={() => SORT_COLS.includes(col) && handleSort(col)}
    >
      {label}
      {SORT_COLS.includes(col) && <SortIcon col={col} />}
    </th>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Table toolbar */}
      <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Campaigns</h3>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 w-56"
            />
          </div>
          {/* Channel filter */}
          <div className="relative">
            <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <select
              value={channelFilter}
              onChange={(e) => { setChannelFilter(e.target.value); setPage(0); }}
              className="pl-8 pr-6 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 appearance-none bg-white cursor-pointer"
            >
              <option value="all">All Channels</option>
              <option value="google">Google</option>
              <option value="meta">Meta</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {colHeader('Channel', 'channel')}
              {colHeader('Campaign Name', 'campaignName')}
              {colHeader('Spend', 'spend')}
              {colHeader('Impr', 'impressions')}
              {colHeader('Clicks', 'clicks')}
              {colHeader('CTR', 'ctr')}
              {colHeader('CPC', 'cpc')}
              {colHeader('Convs', 'conversions')}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {visible.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">
                  No campaigns found
                </td>
              </tr>
            ) : (
              visible.map((c, idx) => {
                const cfg = CHANNEL_CONFIG[c.channel] || {};
                return (
                  <tr key={`${c.channel}-${c.campaignId}-${idx}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <img
                          src={cfg.icon}
                          alt={c.channel}
                          className="w-4 h-4 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cfg.bgColor || 'bg-gray-100'} ${cfg.textColor || 'text-gray-600'}`}>
                          {c.channel}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 max-w-xs">
                      <span className="truncate block text-gray-800 font-medium" title={c.campaignName}>
                        {c.campaignName || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-gray-700">₺{fmtCurrency(c.spend)}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">{fmtNumber(c.impressions)}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">{fmtNumber(c.clicks)}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">{fmtPct(c.ctr)}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">₺{fmtCurrency(c.cpc)}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">{fmtNumber(c.conversions, 0)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
          <span>{filtered.length} campaigns</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
            >
              Prev
            </button>
            <span className="px-2">{page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AppOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('last30');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const abortRef = useRef(null);

  const brandId = localStorage.getItem('currentBrandId');

  const fetchData = useCallback(async () => {
    if (!brandId) return;

    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      appendDateParams(params, dateRange, customStart, customEnd);

      const url = `https://api.glowytics.com/api/app-overview?${params.toString()}`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
        signal: ctrl.signal,
      });

      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`API error ${resp.status}${txt ? ': ' + txt : ''}`);
      }

      const json = await resp.json();
      setData(json);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [brandId, dateRange, customStart, customEnd]);

  useEffect(() => {
    fetchData();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchData]);

  const handleDateChange = (range, start, end) => {
    setDateRange(range);
    setCustomStart(start);
    setCustomEnd(end);
  };

  // ── No brand selected ──
  if (!brandId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Smartphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Please select a brand to view App Overview</p>
        </div>
      </div>
    );
  }

  const s = data?.summary || {};
  const channels = data?.channels || [];
  const campaigns = data?.campaigns || [];
  const trend = data?.trend || [];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* ── Header ── */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">App Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Google Ads + Meta + TikTok app campaigns with Adjust attribution
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeFilter
            value={dateRange}
            customStart={customStart}
            customEnd={customEnd}
            onChange={handleDateChange}
            align="right"
          />
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800">Failed to load data</p>
            <p className="text-xs text-amber-600 mt-0.5 break-all">{error}</p>
          </div>
          <button
            onClick={fetchData}
            className="text-xs font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── KPI Summary ── */}
      {loading && !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            icon={DollarSign}
            label="Total Spend"
            value={`₺${fmtCurrency(s.totalSpend)}`}
          />
          <KpiCard
            icon={Smartphone}
            label="Adjust Installs"
            value={fmtNumber(s.adjustInstalls, 0)}
            subtitle="CPI"
            subtitleValue={`₺${fmtCurrency(s.cpi)}`}
          />
          <KpiCard
            icon={ShoppingCart}
            label="Adjust Purchases"
            value={fmtNumber(s.adjustPurchases, 0)}
            subtitle="CPP"
            subtitleValue={`₺${fmtCurrency(s.cpp)}`}
          />
          <KpiCard
            icon={TrendingUp}
            label="Adjust Revenue"
            value={`₺${fmtCurrency(s.adjustRevenue)}`}
            subtitle="ROAS"
            subtitleValue={fmtRoas(s.roas)}
          />
        </div>
      )}

      {/* ── Per-channel cards ── */}
      {loading && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      ) : channels.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {channels.map((ch) => (
            <ChannelCard key={ch.key} channel={ch} />
          ))}
        </div>
      ) : !loading && (
        <div className="mb-6 p-6 bg-white rounded-xl shadow-sm border border-gray-100 text-center text-gray-400 text-sm">
          No channel data available
        </div>
      )}

      {/* ── Trend Chart ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-800">Daily spend by channel &amp; installs</h3>
        </div>
        {loading && !data ? (
          <Skeleton className="h-56" />
        ) : (
          <div className="h-64">
            <TrendChart trend={trend} />
          </div>
        )}
      </div>

      {/* ── Campaign Table ── */}
      {loading && !data ? (
        <Skeleton className="h-48" />
      ) : campaigns.length === 0 && !loading && !error ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-400 text-sm">No app campaigns found in this date range</p>
        </div>
      ) : (
        <CampaignTable campaigns={campaigns} />
      )}
    </div>
  );
}
